/**
 * auto-fee.js — v3 (23-09-2026)
 *
 * Shipping Protection + Fit Guarantee automatisch in de winkelwagen zolang er een
 * ECHT product in zit, verborgen in de theme-cart, en mee in de Whop/Lasso-checkout.
 *
 * Waarom deze opzet (Lasso v2 patcht de checkout-knop en leest de cart op klikmoment):
 *   1. ADD-GATE      de klik op de add-knop wordt vastgehouden tot de fees bevestigd
 *                    zijn, daarna gaat de klik door. De cart is dus nooit
 *                    "alleen product" op het moment dat de checkout wordt gebouwd.
 *   2. PRE-WARM      pointerdown op de add-knop doet alleen een /cart.js READ (geen
 *                    write), zodat de gate geen extra read op het kritieke pad heeft.
 *   3. SYNC          na de fee-add publiceren we cart-update zodat drawer/footer/badge
 *                    opnieuw renderen; de fee-regels blijven zichtbaar verborgen.
 *   4. CLEANUP       een cart met uitsluitend fees wordt opgeruimd (grace window).
 *   5. GATES         checkout-knop en Lasso/Whop-verzoeken wachten op de bevestiging,
 *                    maar ALLEEN als er al een echt product in de cart zit. Alles
 *                    faalt open na een bounded wachttijd.
 *
 * v3-fixes t.o.v. v2 (zie audit 23-09-2026):
 *   - Geen force-writes meer zonder echte add-klik: de gate vulde bij elke pageview
 *     de fees in een lege cart (Lasso/Whop doen bij load zulke requests) en de
 *     cleanup verwijderde ze 2s later weer. Dat leverde 3 cart-writes per pageview op.
 *   - Fee-add zit niet meer op het kritieke pad van de klik: geen tweede /cart.js-read
 *     meer voordat de gate opent; verificatie + publish lopen op de achtergrond.
 *   - Licht: geen 1s-polling meer (was 20 requests), MutationObserver alleen op de
 *     cart-drawer/cart-items in plaats van documentElement, hide heeft een vroege exit.
 */
(function () {
  'use strict';

  var cfg = window.__autoFeeConfig || {};
  var FEE_VARIANT_IDS = (cfg.feeVariantIds || []).map(Number).filter(function (n) { return !!n; });
  if (!FEE_VARIANT_IDS.length || !window.fetch) return;

  var FEE_QUANTITY = 1;
  var DEBUG = false;
  var CART_UPDATE_EVENT = 'cart-update'; // PUB_SUB_EVENTS.cartUpdate (assets/constants.js)
  var CART_CACHE_TTL_MS = 1200;

  var ADD_SELECTOR = 'button[name="add"], [data-add-to-cart], .replica-pdp__atc, .product-form__submit, replica-sticky-buy button';
  // Quick-add / collectiekaarten: daar nooit fees aan hangen (die tik opent vaak
  // alleen een variant-modal en voegt niets toe).
  var QUICK_ADD_SCOPE = 'modal-opener, quick-add-modal, quick-add-bulk, .quick-add, .quick-add__submit, .card-wrapper, .card, .grid__item';
  var CHECKOUT_SELECTOR = 'button[name="checkout"], input[name="checkout"], [id*="Checkout"] button, [id*="Checkout"], a[href*="/checkout"], .cart__checkout';
  var EXTERNAL_GATE = /lassocheckout|whop|checkout_sessions|cart-sessions/i;

  var EXTERNAL_MAX_WAIT_MS = 1500;  // Lasso/Whop mogen nooit lang hangen
  var ADD_GATE_MAX_WAIT_MS = 2500;  // add-klik nooit langer vasthouden dan dit
  var CLEANUP_GRACE_MS = 5000;

  function log() { if (DEBUG && window.console) console.log.apply(console, ['[auto-fee]'].concat([].slice.call(arguments))); }

  var cartCache = null, cartCacheAt = 0, cartInFlight = null;
  var feesConfirmed = false;
  var addGateBusy = false, allowNextAddClick = false, allowNextCheckout = false;
  var lastAddIntentAt = 0, lastCheckRunAt = 0;

  // Onze eigen calls gaan hier langs: de fetch die Lasso/andere wrappers al zagen.
  var nativeFetch = window.fetch.bind(window);

  /* ---------------- cart state (1 bron, kort gecached) ---------------- */

  function setCart(cart) {
    cartCache = cart || null;
    cartCacheAt = Date.now();
    if (cart) feesConfirmed = hasAllFees(cart);
    return cart;
  }

  function readCart(fresh) {
    var now = Date.now();
    if (!fresh && cartCache && (now - cartCacheAt) < CART_CACHE_TTL_MS) return Promise.resolve(cartCache);
    if (cartInFlight) return cartInFlight;
    cartInFlight = nativeFetch('/cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('cart.js ' + r.status);
        return r.json();
      })
      .then(function (cart) { cartInFlight = null; return setCart(cart); })
      .catch(function (e) { cartInFlight = null; log('cart read faalde', e); return cartCache; });
    return cartInFlight;
  }

  function feeLines(cart) {
    return ((cart && cart.items) || []).filter(function (item) {
      return FEE_VARIANT_IDS.indexOf(Number(item.variant_id)) !== -1;
    });
  }

  function realLines(cart) {
    return ((cart && cart.items) || []).filter(function (item) {
      return FEE_VARIANT_IDS.indexOf(Number(item.variant_id)) === -1;
    });
  }

  function hasAllFees(cart) {
    var lines = feeLines(cart);
    return FEE_VARIANT_IDS.every(function (id) {
      var hits = lines.filter(function (item) { return Number(item.variant_id) === Number(id); });
      return hits.length === 1 && Number(hits[0].quantity) === FEE_QUANTITY;
    });
  }

  function missingFeeIds(cart) {
    var present = ((cart && cart.items) || []).map(function (item) { return Number(item.variant_id); });
    return FEE_VARIANT_IDS.filter(function (id) { return present.indexOf(Number(id)) === -1; });
  }

  /* ---------------- theme cart refresh + verbergen ---------------- */

  function publishCartUpdate(cart) {
    try {
      if (typeof window.publish === 'function') {
        window.publish(CART_UPDATE_EVENT, { source: 'auto-fee', cartData: cart });
        log('cart-update gepubliceerd');
      }
    } catch (e) { log('publish faalde', e); }
    queueHide();
  }

  function hideFeeLines() {
    var sel = FEE_VARIANT_IDS.map(function (id) {
      return '[data-quantity-variant-id="' + id + '"], [data-cart-item-variant-id="' + id + '"]';
    }).join(',');
    var nodes = document.querySelectorAll(sel);
    if (!nodes.length) return; // vroege exit: geen fee-regels in de DOM
    Array.prototype.forEach.call(nodes, function (el) {
      var line = el.closest('tr.cart-item, .cart-item, [id^="CartDrawer-Item-"], [id^="CartItem-"]');
      if (line) line.setAttribute('data-auto-fee-hidden', '');
    });
  }

  var hideQueued = false;
  function queueHide() {
    if (hideQueued) return;
    hideQueued = true;
    (window.requestAnimationFrame || function (cb) { setTimeout(cb, 16); })(function () {
      hideQueued = false;
      hideFeeLines();
    });
  }

  /* ---------------- fee add: alleen waar het echt moet ---------------- */

  // ids: fee-varianten die ontbreken. Wordt alleen aangeroepen (a) tijdens een echte
  // add-klik, of (b) als er een echt product in de cart zit.
  function postFeeAdd(ids) {
    if (!ids.length) return Promise.resolve(null);
    return nativeFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: ids.map(function (id) { return { id: id, quantity: FEE_QUANTITY }; })
      })
    }).then(function (r) {
      return r.text().then(function (t) {
        if (!r.ok) throw new Error('fee add ' + r.status + ' ' + t.slice(0, 120));
        // Server-side staan de fees nu in de cart. De gate mag direct open: de
        // verificatie-read + drawer-refresh lopen op de achtergrond.
        feesConfirmed = true;
        if (cartCache) {
          cartCache.items = (cartCache.items || []).concat(ids.map(function (id) {
            return { variant_id: id, quantity: FEE_QUANTITY };
          }));
          cartCacheAt = Date.now();
        }
        return t;
      });
    });
  }

  function refreshAfterFeeAdd() {
    readCart(true).then(function (cart) {
      if (!cart) return;
      if (hasAllFees(cart)) publishCartUpdate(cart);
      queueHide();
    });
  }

  // Kritieke pad van de add-klik: cache gebruiken, fees toevoegen, klaar.
  function ensureFeesForAdd() {
    return readCart(false)
      .then(function (cart) {
        if (hasAllFees(cart)) { feesConfirmed = true; return null; }
        return postFeeAdd(missingFeeIds(cart));
      })
      .catch(function (e) { log('fee-gate faalde, fail-open', e); })
      .then(function () { refreshAfterFeeAdd(); });
  }

  // Achtergrond-pad: fees horen in de cart te staan zolang er een ECHT product in zit.
  // Zonder echt product wordt er niets toegevoegd (alleen opruimen).
  function topUpIfRealProduct() {
    return readCart(false).then(function (cart) {
      if (!cart) return null;
      var reals = realLines(cart);
      if (!reals.length) {
        if (feeLines(cart).length) return cleanupFeeOnlyCart(cart);
        return null;
      }
      if (hasAllFees(cart)) return null;
      return postFeeAdd(missingFeeIds(cart)).then(function (resp) {
        if (resp) refreshAfterFeeAdd();
      }).catch(function (e) { log('top-up faalde', e); });
    });
  }

  function cleanupFeeOnlyCart(cart) {
    var lines = feeLines(cart);
    if (!lines.length) return null;
    if (realLines(cart).length) return null;              // echt product: niets opruimen
    if (addGateBusy) return null;                         // lopende add: niet ingrijpen
    if (Date.now() - lastAddIntentAt < CLEANUP_GRACE_MS) return null;

    log('cart bevat alleen fees -> opruimen');
    return lines.reduce(function (chain, line) {
      return chain.then(function () {
        return nativeFetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 })
        }).then(function (r) {
          return r.text().then(function (t) {
            if (!r.ok) throw new Error('fee cleanup ' + r.status + ' ' + t.slice(0, 120));
            return t;
          });
        });
      });
    }, Promise.resolve())
      .then(function () { return readCart(true); })
      .then(function (after) {
        if (after) { feesConfirmed = hasAllFees(after); publishCartUpdate(after); }
        return null;
      })
      .catch(function (e) { log('cleanup faalde', e); });
  }

  /* ---------------- add-intentie (read-only pre-warm) ---------------- */

  function intentTarget(el) {
    if (!el || !el.closest) return null;
    if (el.closest(QUICK_ADD_SCOPE)) return null;         // quick-add/collectie: nooit
    var ctrl = el.closest(ADD_SELECTOR);
    if (!ctrl) return null;
    // Alleen echte add-knoppen van het product-form of de sticky buy-bar.
    if (!ctrl.closest('form[action*="/cart/add"], product-form, replica-sticky-buy') &&
        !document.querySelector('main[data-template="product"]')) return null;
    return ctrl;
  }

  document.addEventListener('pointerdown', function (e) {
    var ctrl = intentTarget(e.target);
    if (!ctrl) return;
    lastAddIntentAt = Date.now();
    readCart(false);      // READ-ONLY pre-warm (geen write, geen fee-add)
  }, true);

  /* ---------------- add-gate ---------------- */

  function onAddClick(e) {
    var ctrl = intentTarget(e.target);
    if (!ctrl) return;
    lastAddIntentAt = Date.now();

    if (allowNextAddClick) { allowNextAddClick = false; return; }
    if (feesConfirmed) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    if (addGateBusy) return;

    addGateBusy = true;
    ctrl.setAttribute('data-auto-fee-waiting', '');
    var guard = setTimeout(function () {                 // fail-open, altijd
      if (!addGateBusy) return;
      addGateBusy = false;
      ctrl.removeAttribute('data-auto-fee-waiting');
      allowNextAddClick = true;
      ctrl.click();
    }, ADD_GATE_MAX_WAIT_MS);

    ensureFeesForAdd().then(function () {
      clearTimeout(guard);
      addGateBusy = false;
      lastAddIntentAt = Date.now();
      ctrl.removeAttribute('data-auto-fee-waiting');
      allowNextAddClick = true;
      log('add-gate open, product toevoegen');
      ctrl.click();
    });
  }
  window.addEventListener('click', onAddClick, true);

  /* ---------------- checkout-gate ---------------- */

  function hasRealProductCached() {
    return !!(cartCache && realLines(cartCache).length);
  }

  function waitForFees(maxMs) {
    var started = Date.now();
    return new Promise(function (resolve) {
      (function step() {
        if (feesConfirmed) return resolve(true);
        if (Date.now() - started > maxMs) return resolve(false);
        readCart(false).then(function () { setTimeout(step, 120); });
      })();
    });
  }

  function releaseGate(ctrl) {
    if (!ctrl) return;
    ctrl.removeAttribute('aria-disabled');
    ctrl.removeAttribute('data-auto-fee-waiting');
    if (ctrl.tagName === 'BUTTON' || ctrl.tagName === 'INPUT') ctrl.disabled = false;
  }

  window.addEventListener('click', function (e) {
    var ctrl = e.target && e.target.closest && e.target.closest(CHECKOUT_SELECTOR);
    if (!ctrl) return;
    if (allowNextCheckout) { allowNextCheckout = false; return; }
    if (feesConfirmed || !hasRealProductCached()) return;   // geen echt product: niets vasthouden

    e.preventDefault();
    e.stopImmediatePropagation();
    ctrl.setAttribute('aria-disabled', 'true');
    ctrl.setAttribute('data-auto-fee-waiting', '');
    if (ctrl.tagName === 'BUTTON' || ctrl.tagName === 'INPUT') ctrl.disabled = true;

    topUpIfRealProduct();          // echt product aanwezig: fees mogen hier gevuld worden
    waitForFees(EXTERNAL_MAX_WAIT_MS).then(function (ok) {
      releaseGate(ctrl);
      allowNextCheckout = true;
      log(ok ? 'checkout-gate open' : 'checkout-gate timeout, fail-open');
      ctrl.click();
    });
  }, true);

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.getAttribute) return;
    if ((form.getAttribute('action') || '').indexOf('/checkout') === -1) return;
    if (allowNextCheckout) { allowNextCheckout = false; return; }
    if (feesConfirmed || !hasRealProductCached()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    topUpIfRealProduct();
    waitForFees(EXTERNAL_MAX_WAIT_MS).then(function () {
      allowNextCheckout = true;
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    });
  }, true);

  /* ---------------- Lasso/Whop-gate: wachten, nooit vullen ---------------- */

  var of = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var u = args[0];
    var url = typeof u === 'string' ? u : (u && u.url) || '';
    var isCart = typeof url === 'string' && /\/cart\/(add|update|change|clear)/.test(url);

    if (EXTERNAL_GATE.test(url) && !feesConfirmed && hasRealProductCached()) {
      // Alleen wachten als er écht een product in de cart zit. Geen force-add:
      // waitForFees() leest alleen, en topUpIfRealProduct() mag hier wel vullen.
      topUpIfRealProduct();
      return waitForFees(EXTERNAL_MAX_WAIT_MS).then(function () { return of.apply(window, args); });
    }
    return of.apply(window, args).then(function (res) {
      if (isCart) {
        if (/\/cart\/clear/.test(url)) { feesConfirmed = false; cartCache = null; cartCacheAt = 0; }
        setTimeout(function () { topUpIfRealProduct().then(queueHide); }, 250);
      }
      return res;
    });
  };

  var oOpen = XMLHttpRequest.prototype.open;
  var oSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (m, u) { this.__autoFeeUrl = u; return oOpen.apply(this, arguments); };
  XMLHttpRequest.prototype.send = function () {
    var self = this;
    var sendArgs = arguments;
    var url = self.__autoFeeUrl || '';
    if (typeof url === 'string' && /\/cart\/(add|update|change|clear)/.test(url)) {
      this.addEventListener('load', function () {
        setTimeout(function () { topUpIfRealProduct().then(queueHide); }, 250);
      });
    }
    if (typeof url === 'string' && EXTERNAL_GATE.test(url) && !feesConfirmed && hasRealProductCached()) {
      topUpIfRealProduct();
      waitForFees(EXTERNAL_MAX_WAIT_MS).then(function () { oSend.apply(self, sendArgs); });
      return;
    }
    return oSend.apply(this, arguments);
  };

  /* ---------------- boot: licht en event-driven ---------------- */

  function check(force) {
    var now = Date.now();
    if (!force && (now - lastCheckRunAt) < CART_CACHE_TTL_MS) return;
    lastCheckRunAt = now;
    topUpIfRealProduct().then(queueHide);
  }

  function boot() { check(true); queueHide(); }

  boot();
  document.addEventListener('DOMContentLoaded', function () { check(false); queueHide(); });
  // Vangnet i.p.v. de oude 1s-polling (20 requests): 2 extra checks, niet meer.
  setTimeout(function () { check(true); }, 2500);

  if (typeof window.subscribe === 'function') {
    try { window.subscribe(CART_UPDATE_EVENT, function () { queueHide(); }); } catch (e) {}
  }
  document.addEventListener('visibilitychange', function () { if (!document.hidden) check(false); });

  if (window.MutationObserver) {
    var startObserver = function () {
      var root = document.querySelector('cart-drawer') || document.querySelector('cart-items') || document.body;
      var mo = new MutationObserver(function () { queueHide(); });
      mo.observe(root, { childList: true, subtree: true });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startObserver);
    else startObserver();
  }

  log('auto-fee v3 geladen:', FEE_VARIANT_IDS.join(','));
})();
